/**
 * StorylineModal — Create (and, later, Edit) a Storyline (storyline-design.md M5,
 * Figma 3246:45128). Fields: Name / Industry / Choose Assets. Create and Edit share
 * this one modal; only the prefill + which fields are editable + the submit op differ.
 * This CR wires the CREATE path (industry-row `+` in the Storyline Menu tree); the
 * edit path (`mode='edit'`) is structurally supported but not yet triggered anywhere.
 *
 * - Create: industry prefilled from the triggering row, assets empty.
 * - Edit: name + linked assets prefilled; industry read-only (cross-industry move is
 *   not supported — build a new storyline instead, per backend contract).
 *
 * On confirm: CreateStoryline → server generates the id → onCreated(id) so the caller
 * navigates to the new detail page. Choose Assets is scoped to the selected industry
 * (an asset's industry must equal the storyline's, backend-enforced).
 */
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  Multiselect,
  Select,
  SpaceBetween,
  type MultiselectProps,
  type SelectProps,
} from '@amzn/awsui-components-react';
import type { AssetSummary } from '../../../client/IvtHubClient';
import { storylineClient } from '../../../client/storylineClient';
import { denialHint } from '../../../utils/authorization';

export interface StorylineModalProps {
  /** create = industry prefilled, assets empty; edit = all prefilled, industry locked. */
  mode: 'create' | 'edit';
  /** Every asset (for the Choose Assets options, filtered to the selected industry). */
  assets: AssetSummary[];
  /** Industry universe (ListIndustries names) for the Industry select. */
  industries: string[];
  /** Prefill. create: only `industry` (from the triggering row). edit: all three +
   *  `storylineId` (required in edit mode for the update call). */
  initial?: { storylineId?: string; name?: string; industry?: string; assetIds?: string[] };
  onClose: () => void;
  /** Called with the created/updated storyline id on success. */
  onSaved: (storylineId: string) => void;
}

export default function StorylineModal({
  mode,
  assets,
  industries,
  initial,
  onClose,
  onSaved,
}: StorylineModalProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [industry, setIndustry] = useState(initial?.industry ?? '');
  const [assetIds, setAssetIds] = useState<string[]>(initial?.assetIds ?? []);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = mode === 'edit';

  const industryOptions: SelectProps.Option[] = useMemo(
    () => industries.map((i) => ({ value: i, label: i })),
    [industries],
  );

  // Choose Assets options = assets in the selected industry (storyline is per-industry;
  // an asset's industry must equal the storyline's, so cross-industry assets can't link).
  const assetOptions: MultiselectProps.Option[] = useMemo(
    () =>
      assets
        .filter((a) => a.industry === industry && a.assetId)
        .map((a) => ({ value: a.assetId as string, label: a.name ?? a.useCase ?? (a.assetId as string) })),
    [assets, industry],
  );

  const selectedAssetOptions = useMemo(
    () => assetOptions.filter((o) => o.value && assetIds.includes(o.value)),
    [assetOptions, assetIds],
  );

  const canConfirm = name.trim().length > 0 && industry.length > 0 && !submitting;

  async function handleConfirm() {
    if (!canConfirm) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit) {
        if (!initial?.storylineId) throw new Error('missing storylineId');
        await storylineClient.update({ storylineId: initial.storylineId, name: name.trim(), assetIds });
        onSaved(initial.storylineId);
      } else {
        const created = await storylineClient.create({
          name: name.trim(),
          industry,
          description: '',
          assetIds: assetIds.length ? assetIds : undefined,
        });
        if (!created?.id) throw new Error('no id returned');
        onSaved(created.id);
      }
      onClose();
    } catch (e) {
      // Name-collision within the industry surfaces as ConflictException.
      const kind = (e as { name?: string }).name ?? '';
      const denied = denialHint(e);
      setError(
        kind === 'ConflictException'
          ? `该行业下已存在名为「${name.trim()}」的 Storyline，请换一个名字。`
          : denied ?? `${isEdit ? '更新' : '创建'} Storyline 失败，请重试。`,
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible
      onDismiss={() => !submitting && onClose()}
      size="medium"
      header={isEdit ? 'Edit Storyline' : 'Create New Storyline'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" disabled={submitting} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" loading={submitting} disabled={!canConfirm} onClick={() => void handleConfirm()}>
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Box color="text-status-error" fontSize="body-s">
            {error}
          </Box>
        )}
        <FormField label="Storyline Name" description="行业内的销售故事线名称">
          <Input
            value={name}
            onChange={({ detail }) => setName(detail.value)}
            placeholder="Placeholder"
            disabled={submitting}
          />
        </FormField>
        <FormField label="Industry">
          <Select
            selectedOption={industry ? { value: industry, label: industry } : null}
            onChange={({ detail }) => {
              // Changing industry invalidates the asset picks (they were scoped to the
              // old industry). Reset selected assets so we never submit cross-industry links.
              setIndustry(detail.selectedOption.value ?? '');
              setAssetIds([]);
            }}
            options={industryOptions}
            placeholder="选择行业"
            // Industry is always read-only: create prefills it from the triggering `+`
            // row, edit can't move industries (would invalidate every link). Either way
            // a storyline belongs to exactly one, fixed industry.
            disabled
          />
        </FormField>
        <FormField label="Choose Assets" description={industry ? undefined : '先选择行业'}>
          <Multiselect
            selectedOptions={selectedAssetOptions}
            onChange={({ detail }) =>
              setAssetIds(detail.selectedOptions.map((o) => o.value).filter((v): v is string => !!v))
            }
            options={assetOptions}
            placeholder="Choose options"
            disabled={!industry || submitting}
            filteringType="auto"
            empty={industry ? '该行业下暂无 Asset' : '先选择行业'}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
